// React & React Hooks
import { useContext, useState } from "react";

// Third-party libraries
import { withTranslation } from "react-i18next";
import { useForm } from "react-hook-form";

// Context / Providers
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";

// MUI components
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  Stack,
} from "@mui/material";

// MUI icons
import CloseIcon from "@mui/icons-material/Close";

// Constants
import dataConstant from "../../constants/dataConstants";
import urlConstant from "../../constants/urlConstants";

// Services / utilities
import { httpPost } from "../../services/commonService";

// Custom components
import CommonSnackBar from "../snackbar/commonSnackbar";
import SnackState from "../snackbar/snackState";
import CustomTextField from "../form-controls/customTextField";


const UpdateUserInfo = (props) => {
  const { modalVisible, userId, cancelCallbackMethod, t } = props;
  const { accessToken, impersonateEmail, logUserOut } = useContext(
    AuthenticationContext
  );
  const [modalSuccessVisible, setModalSuccessVisible] = useState(false);
  const [snack, closeSnack, showSnackbar] = SnackState();

  const { handleSubmit, setValue, ...formMethods } = useForm({
    mode: "all",
    defaultValues: {
      email: "",
    },
  });

  const formConfig = {
    emailField: {
      name: "email",
      label: t("PrivateInfo.UpdateUserInformation.New Email Address"),
      type: "email",
      maxLength: dataConstant.MinMaxLength.CharLimit50 || 100,
      required: true,
      placeholder: t(
        "PrivateInfo.UpdateUserInformation.Enter new email address"
      ),
    },
  };

  const handleCancel = () => {
    cancelCallbackMethod?.();
  };

  const onSubmit = async (data) => {
    const modelData = {
      UserId: userId,
      NewEmail: data.email.toLowerCase(),
    };

    try {
      const response = await httpPost(
        urlConstant.UserManagement.EditUserLoginInfo,
        modelData,
        true,
        true,
        accessToken,
        impersonateEmail
      );

      switch (response.Status) {
        case dataConstant.APIResponseStatus.Success:
          handleCancel();
          setModalSuccessVisible(true);
          break;

        case dataConstant.APIResponseStatus.Failure:
          showSnackbar?.(response.description, "failure");
          cancelCallbackMethod?.();
          break;

        default:
          console.warn("Unhandled response status:", response.Status);
      }
    } catch (error) {
      console.error("Error updating user email:", error);
    }
  };

  return (
    <>
      <Dialog open={modalSuccessVisible} onClose={logUserOut}>
        <Stack
          sx={(theme) => ({
            background: theme.palette.common.black,
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
          })}
        >
          <DialogTitle>
            {t("PrivateInfo.UpdateUserInformation.Login Email Changed")}
            <IconButton
              aria-label="close"
              onClick={logUserOut}
              className="close-icon-bg"
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
                p: 1,
                borderRadius: "8px",
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
        </Stack>
        <DialogContent
          sx={{
            background: "var(--background-card)!important",
            border: 0,
            borderBottomLeftRadius: "20px",
            borderBottomRightRadius: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
          dividers
        >
          <Typography>
            {t(
              "PrivateInfo.UpdateUserInformation.Login email changed Please log in again"
            )}
          </Typography>
          <Stack direction={"row"} justifyContent={"flex-end"}>
            <Button
              onClick={logUserOut}
              sx={{ px: 3, mt: 1, width: { xs: "100%", sm: "auto" } }}
              color="primary"
              variant="contained"
            >
              {t("Common.Ok")}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={modalVisible} fullWidth onClose={handleCancel}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack
            className="popup-title-icon"
            sx={(theme) => ({
              background: theme.palette.common.black,
            })}
          >
            <DialogTitle>
              {t(
                "PrivateInfo.UpdateUserInformation.Update User Login Information"
              )}
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={handleCancel}
              className="close-icon-bg"
              sx={(theme) => ({
                position: "absolute",
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
              })}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          <DialogContent
            sx={(theme) => ({
              background: theme.palette.background.cardGradient,
              border: 0,
              borderBottomLeftRadius: "20px",
              borderBottomRightRadius: "20px",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              alignItems: "flex-start",
            })}
            dividers
          >
            <Alert
              sx={(theme) => ({
                background: theme.palette.common.errorbg,
                color: theme.palette.common.errortext,
                width: "100%",
              })}
              variant="filled"
              severity="info"
            >
              {t("PrivateInfo.UpdateUserInformation.New Email")}
            </Alert>
            <Box width={"100%"}>
              <CustomTextField
                methods={{ ...formMethods, setValue }}
                properties={formConfig.emailField}
              />
            </Box>
            <Stack
              width={"100%"}
              borderTop={"1px solid var(--headerbg)"}
              justifyContent={"space-between"}
              pt={"20px"}
              gap={2}
              sx={{ flexDirection: { xs: "column-reverse", sm: "row" } }}
            >
              <Button
                onClick={handleCancel}
                color="secondary"
                variant="contained"
                startIcon={<CloseIcon />}
                sx={{ px: 3, mx: { xs: "auto", sm: "initial" } }}
              >
                {t("Common.Cancel")}
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                sx={{ px: 3 }}
              >
                {t("Common.Update")}
              </Button>
            </Stack>
          </DialogContent>
        </form>
      </Dialog>

      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
    </>
  );
};

export default withTranslation()(UpdateUserInfo);